import React from 'react';
import { Button, ButtonProps, useTheme, CircularProgress } from '@mui/material';

interface PrimaryButtonProps extends Omit<ButtonProps, 'variant' | 'color'> {
  /**
   * Variante do botão
   * @default 'contained'
   */
  variant?: 'contained' | 'outlined' | 'text';
  /**
   * Cor do botão
   * @default 'primary'
   */
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  /**
   * Tamanho do botão
   * @default 'medium'
   */
  size?: 'small' | 'medium' | 'large';
  /**
   * Se deve ocupar toda a largura disponível
   * @default false
   */
  fullWidth?: boolean;
  /**
   * Estado de carregamento do botão
   * @default false
   */
  loading?: boolean;
}

/**
 * Componente de botão primário padronizado seguindo o tema da aplicação
 */
const PrimaryButton: React.FC<PrimaryButtonProps> = ({
  children,
  variant = 'contained',
  color = 'primary',
  size = 'medium',
  fullWidth = false,
  loading = false,
  disabled,
  sx,
  ...props
}) => {
  const theme = useTheme();

  const getSpinnerSize = () => {
    return size === 'small' ? 16 : size === 'large' ? 24 : 20;
  };

  const getSpinnerColor = () => {
    if (variant === 'contained') {
      return color === 'primary' ? theme.palette.primary.contrastText :
             color === 'secondary' ? theme.palette.secondary.contrastText :
             color === 'error' ? theme.palette.error.contrastText : 
             theme.palette.primary.contrastText;
    } else {
      return color === 'primary' ? theme.palette.primary.main :
             color === 'secondary' ? theme.palette.secondary.main :
             color === 'error' ? theme.palette.error.main :
             theme.palette.primary.main;
    }
  };

  const getButtonStyles = () => {
    const baseStyles = {
      borderRadius: '8px',
      textTransform: 'none' as const,
      fontWeight: 500,
      transition: 'all 0.3s ease',
      minHeight: size === 'small' ? 32 : size === 'large' ? 48 : 40,
      fontSize: size === 'small' ? '0.875rem' : size === 'large' ? '1.125rem' : '1rem',
    };

    if (variant === 'contained') {
      if (color === 'primary') {
        return {
          ...baseStyles,
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          boxShadow: '0 4px 8px rgba(4, 46, 75, 0.25)',
          '&:hover': {
            backgroundColor: theme.palette.primary.dark,
            boxShadow: '0 6px 12px rgba(4, 46, 75, 0.35)',
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'translateY(0px)',
            boxShadow: '0 2px 4px rgba(4, 46, 75, 0.35)',
          },
          '&:disabled': {
            backgroundColor: theme.palette.action.disabledBackground,
            color: theme.palette.action.disabled,
            boxShadow: 'none',
            transform: 'none',
          },
        };
      } else if (color === 'secondary') {
        return {
          ...baseStyles,
          backgroundColor: theme.palette.secondary.main,
          color: theme.palette.secondary.contrastText,
          boxShadow: '0 4px 8px rgba(239, 69, 61, 0.25)',
          '&:hover': {
            backgroundColor: theme.palette.secondary.dark,
            boxShadow: '0 6px 12px rgba(239, 69, 61, 0.35)',
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'translateY(0px)',
            boxShadow: '0 2px 4px rgba(239, 69, 61, 0.35)',
          },
          '&:disabled': {
            backgroundColor: theme.palette.action.disabledBackground,
            color: theme.palette.action.disabled,
            boxShadow: 'none',
            transform: 'none',
          },
        };
      } else if (color === 'error') {
        return {
          ...baseStyles,
          backgroundColor: theme.palette.error.main,
          color: theme.palette.error.contrastText,
          boxShadow: '0 4px 8px rgba(211, 47, 47, 0.25)',
          '&:hover': {
            backgroundColor: theme.palette.error.dark,
            boxShadow: '0 6px 12px rgba(211, 47, 47, 0.35)',
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'translateY(0px)',
            boxShadow: '0 2px 4px rgba(211, 47, 47, 0.35)',
          },
          '&:disabled': {
            backgroundColor: theme.palette.action.disabledBackground,
            color: theme.palette.action.disabled,
            boxShadow: 'none',
            transform: 'none',
          },
        };
      }
    } else if (variant === 'outlined') {
      const borderColor = color === 'primary' ? theme.palette.primary.main : 
                          color === 'secondary' ? theme.palette.secondary.main : 
                          color === 'error' ? theme.palette.error.main : theme.palette.primary.main;
      const textColor = color === 'primary' ? theme.palette.primary.main : 
                        color === 'secondary' ? theme.palette.secondary.main : 
                        color === 'error' ? theme.palette.error.main : theme.palette.primary.main;
      
      return {
        ...baseStyles,
        backgroundColor: 'transparent',
        color: textColor,
        border: `2px solid ${borderColor}`,
        '&:hover': {
          backgroundColor: color === 'primary' 
            ? `${theme.palette.primary.main}08` 
            : color === 'secondary' 
            ? `${theme.palette.secondary.main}08`
            : `${theme.palette.error.main}08`,
          borderColor: color === 'primary' ? theme.palette.primary.dark : 
                       color === 'secondary' ? theme.palette.secondary.dark : 
                       theme.palette.error.dark,
          transform: 'translateY(-1px)',
        },
        '&:active': {
          transform: 'translateY(0px)',
        },
        '&:disabled': {
          borderColor: theme.palette.action.disabled,
          color: theme.palette.action.disabled,
          transform: 'none',
        },
      };
    } else if (variant === 'text') {
      const textColor = color === 'primary' ? theme.palette.primary.main : 
                        color === 'secondary' ? theme.palette.secondary.main : 
                        color === 'error' ? theme.palette.error.main : theme.palette.primary.main;
      
      return {
        ...baseStyles,
        backgroundColor: 'transparent',
        color: textColor,
        padding: '6px 16px',
        '&:hover': {
          backgroundColor: color === 'primary' 
            ? `${theme.palette.primary.main}08` 
            : color === 'secondary' 
            ? `${theme.palette.secondary.main}08`
            : `${theme.palette.error.main}08`,
        },
        '&:disabled': {
          color: theme.palette.action.disabled,
        },
      };
    }

    return baseStyles;
  };

  return (
    <Button
      variant={variant}
      color={color}
      size={size}
      fullWidth={fullWidth}
      disabled={disabled || loading}
      sx={{
        ...getButtonStyles(),
        ...sx,
      }}
      {...props}
    >
      {loading ? (
        <>
          <CircularProgress 
            size={getSpinnerSize()} 
            sx={{ 
              color: getSpinnerColor(),
              marginRight: children ? 1 : 0
            }} 
          />
          {children}
        </>
      ) : (
        children
      )}
    </Button>
  );
};

export default PrimaryButton;
